import React from 'react';
import {
  Animated,
  TouchableOpacityProps,
  TouchableOpacity
} from 'react-native';
import { GenericTouchableProps } from 'react-native-gesture-handler/lib/typescript/components/touchables/GenericTouchable';
import styled from 'styled-components/native';

import Colors from '../constants/Colors';

const StyledButtonView = styled(Animated.View)<
  ButtonProps & { height: number }
>`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: ${p => p.height}px;
  border-radius: 8px;
  padding: ${p =>
    p.size === 'normal'
      ? `0 ${p.endIcon ? '16px' : '24px'} 0 ${p.startIcon ? '16px' : '24px'}`
      : `0 ${p.endIcon ? '8px' : '16px'} 0 ${p.startIcon ? '8px' : '16px'}`};
  background-color: ${p =>
    p.disabled
      ? Colors.buttonDisabled
      : p.inverse
      ? Colors.buttonInverseBackground
      : p.color === 'secondary'
      ? Colors.secondary
      : p.color
      ? p.color
      : Colors.primary};
  width: ${p => (p.fullWidth ? '100%' : 'auto')};
` as React.FC<ButtonProps & { height: number }>;

const StyledOutlineButtonView = styled(StyledButtonView)`
  background-color: transparent;
  border: 2.25px solid
    ${p =>
      p.disabled
        ? p.color === 'secondary'
          ? '#EFEDFE'
          : Colors.buttonOutlineDisabled
        : p.inverse
        ? Colors.buttonInverseBackground
        : p.color === 'secondary'
        ? Colors.secondary
        : Colors.primary};
` as React.FC<ButtonProps>;

const StyledStartIcon = styled.View`
  margin-right: 12px;
`;

const StyledEndIcon = styled.View`
  margin-left: 12px;
`;

const StyledText = styled.Text<ButtonProps>`
  font-family: 'DMSans-Bold';
  font-size: ${p => (p.size === 'small' ? 12 : 16)}px;
  color: ${p =>
    p.disabled
      ? Colors.buttonDisabledText
      : p.inverse
      ? Colors.primary
      : p.color === 'secondary' || p.color
      ? Colors.secondaryText
      : Colors.primaryText};
  width: ${p => (p.fullWidth ? '100%' : 'auto')};
  text-align: center;
  letter-spacing: ${p => p.letterSpacing};
`;

const StyledOutlineText = styled(StyledText)`
  color: ${p =>
    p.disabled
      ? p.color === 'secondary'
        ? '#392e9e9a'
        : Colors.buttonOutlineDisabled
      : p.inverse
      ? Colors.primaryText
      : p.color === 'secondary'
      ? Colors.secondaryText
      : Colors.primary};
  width: ${p => (p.fullWidth ? '100%' : 'auto')};
  text-align: center;
` as React.FC<ButtonProps>;

export type ButtonProps = TouchableOpacityProps &
  Omit<GenericTouchableProps, 'onPress'> & {
    size?: 'normal' | 'medium' | 'small' | number;
    color?: 'primary' | 'secondary' | string;
    inverse?: boolean;
    outlined?: boolean;
    fullWidth?: boolean;
    disabled?: boolean;
    startIcon?: JSX.Element | undefined | false;
    endIcon?: JSX.Element | undefined | false;
    letterSpacing?: number;
    // variant?: 'text' | 'contained' | 'outlined';
  };

/**
 * Themed button supporting different display behaviors. Do not make changes to
 * this component without further discussion. The default state should be an
 * oval shaped filled button. The alternate is an outlined button using the
 * `outlined` props. The `inverse` prop is used on dark background to show a
 * white version.
 * @param size 'normal' | 'medium' | 'small' | nubmer
 * @param color 'primary' | 'secondary'
 * @param inverse when the button is displayed on dark background
 * @param outlined to display a transparent button with an outline
 * @param fullWidth to fill the width of the container
 * @param disabled place the button in disabled state
 * @param startIcon place icon at the beginning of the button
 * @param endIcon place icon at the end of the button
 * @param onPress
 * @param letterSpacing button text letter-spacing
 */
const Button: React.FC<ButtonProps> = ({
  children,
  outlined,
  startIcon,
  endIcon,
  onPress,
  ...props
}) => {
  let ButtonView, Text;
  const { size, color, inverse, fullWidth, disabled } = props;
  if (outlined || (disabled && color === 'secondary')) {
    ButtonView = StyledOutlineButtonView;
    Text = StyledOutlineText;
  } else {
    ButtonView = StyledButtonView;
    Text = StyledText;
  }
  const height = (() => {
    if (typeof size === 'number') return size;
    switch (size) {
      case 'medium':
        return 40;
      case 'small':
        return 40;
      case 'normal':
      default:
        return 52;
    }
  })();
  const animatedButtonScale = new Animated.Value(1);
  return (
    <TouchableOpacity
      activeOpacity={0.8}
      onPress={onPress}
      onPressIn={() => {
        Animated.spring(animatedButtonScale, {
          toValue: 1.03,
          useNativeDriver: false,
          speed: 12
        }).start();
      }}
      onPressOut={() => {
        Animated.spring(animatedButtonScale, {
          toValue: 1,
          speed: 15,
          useNativeDriver: false
        }).start();
      }}
      {...props}
    >
      <ButtonView
        size={size}
        color={color}
        inverse={inverse}
        fullWidth={fullWidth}
        disabled={disabled}
        height={height}
        startIcon={startIcon}
        endIcon={endIcon}
        style={[{ transform: [{ scale: animatedButtonScale }] }] as any}
      >
        {startIcon && <StyledStartIcon>{startIcon}</StyledStartIcon>}
        <Text
          letterSpacing={props.letterSpacing || 0}
          color={color}
          inverse={inverse}
          disabled={disabled}
          size={size}
          fullWidth={props.fullWidth}
        >
          {children}
        </Text>
        {endIcon && <StyledEndIcon>{endIcon}</StyledEndIcon>}
      </ButtonView>
    </TouchableOpacity>
  );
};

export default Button;
